import http from "./http-common"
import {QueryModel} from "../pages/main/query/query.model";
import { ProductModel } from "../models/Product.model";

export type BodyRegionResponse = {
  id_Body: number,
  name_Body: string,
}
export const getBodyRegions = () => http.get('/bodies')

export type UseTypeResponse = {
  id_TypeUse: number,
  name_TypeUse: string,
}
export const getUseTypes = () => http.get('/typeUse')

export type ActiveResponse = {
  id: number,
  name: string,
  enabled?: boolean,
  actions?: any,
}
export const getActives = () => http.get('/actives')

export const getActivesPaginated = (page: number, size: number) => http.get(`/actives?size=${size}&page=${page}`)
export const enableActive = (id: number) => http.put(`/actives/${id}/enable`)
export const disableActive = (id: number) => http.put(`/actives/${id}/disable`)
export const newActive = (name: string) => http.post(`/actives`, { name })
export const editActive = (id: number, name: string) => http.put(`/actives/${id}`, { name })

// region Subclass
export const getSubclassPaginated = (page: number, size: number) => http.get(`/subclass?size=${size}&page=${page}`)
export const enableSubclass = (id: number) => http.put(`/subclass/${id}/enable`)
export const disableSubclass = (id: number) => http.put(`/subclass/${id}/disable`)
export const newSubclass = (name: string) => http.post(`/subclass`, { name })
export const editSubclass = (id: number, name: string) => http.put(`/subclass/${id}`, { name })

export type SubClassModel = {
  id: number,
  name: string,
  enabled?: boolean,
  actions?: any
}
// endregion

export interface AddPatientEntries {
  name: string,
  observation?: string
}
export const addPatient = (entries: AddPatientEntries) => http.post('/patient', entries)

export interface PatientResponse2 {
  "id_Patient": number,
  "name_Patient": string,
  "aestheticConsultations": number[],
  "observation_Patient": string,
}
export const getPatientsFromUser = (idUser: number) => http.get(`/patients/${idUser}`)

export type SubclassesEntries = {actives: number[]}
export type SubClassesObject = {
  id: number,
  name: string,
}
export type SubclassesResponse = {
  [key: string]: SubClassesObject[]
}
export const getSubclasses = (entries: SubclassesEntries) => {
  return http.post('/subclass/actives', entries)
}

export const canUserConsult = (idUser: number) => http.get(`/user/plan/available/${idUser}`)

export interface SaveConsultationEntries {
  "id_Body": number,
  "id_TypeUse": number,
  "daytime": boolean,
  "nighttime": boolean,
  "active": number[],
  "subclass": number[],
  "pregnant": boolean,
  "vegan": boolean,
  "organic": boolean,
  "id_User": number,
  "id_Patient"?: number,
  "patientIds"?: number[],
  "product": {
    "id": number,
    "percentage": string
  }[],
  vehicles_id?: number,
  observation?: string,
  observations?: string[],
  consultName?: string
}

export const queryToConsultation = (
    model: QueryModel, userId: number, observation: string, observations: string[], products: ProductModel[], consultName?: string
) => {
  const both = model.period === 'AMBOS';

  const productClasses = products
    .flatMap(p =>
      p.classes
        ?.split(/,/)
        .map(c => c.trim().toUpperCase())
        .filter(Boolean) || []
    )
    .filter(Boolean);

  const filteredActives = model.actives.filter(active =>
    productClasses.includes(active.name.toUpperCase().trim())
  );

  const subclass = model.subclass?.length ? model.subclass.map(sub => sub.id) : [];

  const baseConsultation = {
    id_Body: 2,
    id_TypeUse: 1,
    daytime: model.period === 'DIURNO' || both,
    nighttime: model.period === 'NOTURNO' || both,
    active: filteredActives.map(act => act.id),
    subclass: subclass,
    pregnant: model.pregnant || false,
    vegan: model.vegan || false,
    organic: model.organic || false,
    id_User: userId,
    product: products.map(prod => ({
      id: prod.idProduct,
      percentage: prod.percentage || 0,
    })),
    vehicles_id: model.vehicles?.id || null,
    observation: observation,
    observations: observations,
    consultName: consultName
  };

  if (model.patientIds && model.patientIds.length > 0) {
    return {
      ...baseConsultation,
      patientIds: model.patientIds
    } as SaveConsultationEntries;
  } else {
    return {
      ...baseConsultation,
      id_Patient: model.patientId === 0 ? undefined : model.patientId
    } as SaveConsultationEntries;
  }
}

export type SaveConsultationResponse = 
  | { id: number }
  | { consultations: { patientId: number; consultationId: number }[]; totalCreated: number }

export const saveConsultation = (entries: SaveConsultationEntries) => http.post<SaveConsultationResponse>('/aestheticConsultation', entries)

export interface PDFData {
  daytime: boolean;
  nighttime: boolean;
  pregnant: boolean;
  vegan: boolean;
  organic: boolean;
  user: number;
  patient?: number;
  products: Product[];
  vehicleName?: string;
  observation?: string;
  observations?: string[];
}

export interface Product {
  name: string;
  percentage: string;
  showConcentration?: boolean;
}

export const getPdfByConsultationId = (consults:{consults: {consultationId: number, type: string}[]}, skipe: boolean) => {
  return http.post("/v2/pdf", consults)
}

export const getHtmlByConsultationId = (consults:{consults: {consultationId: number, type: string}[], pregnant?: boolean}) => {
  return http.post("/v2/pdf/html", consults)
}

export const duplicateTopicConsultation = (id: number, patients: number[]) => {
  return http.post(`/aestheticConsultation/${id}/duplicate-many`, { patients })
}

export const duplicateOralConsultation = (id: number, patients: number[]) => {
  return http.post(`/oral-consultation/${id}/duplicate-many/`, { patients })
}

// region Batch Consultation V2

export interface BatchConsultationResult {
  patientId: number
  consultationId?: number
  error?: string
}

export interface BatchConsultationSummary {
  total: number
  created: number
  failed: number
}

export interface BatchConsultationResponse {
  message: string
  results: BatchConsultationResult[]
  summary: BatchConsultationSummary
}

export interface ProductData {
  id: number
  percentage: number
}

export interface AestheticConsultationData {
  id_Body: number
  id_TypeUse: number
  consultName: string
  pregnant: boolean
  daytime: boolean
  nighttime: boolean
  vegan: boolean
  organic: boolean
  vehicles_id?: number
  observation?: string
  active: number[]
  subclass: number[]
  product: ProductData[]
  observations?: string[]
}

export interface BatchAestheticConsultationRequest {
  patients: number[]
  consultation: AestheticConsultationData
}

export const createBatchAestheticConsultation = (request: BatchAestheticConsultationRequest) => {
  return http.post<BatchConsultationResponse>('/v2/aesthetic-consultation/batch', request)
}

export const queryToBatchConsultation = (
  model: QueryModel,
  userId: number,
  observation: string,
  observations: string[],
  products: ProductModel[],
  patients: number[],
  consultName?: string
): BatchAestheticConsultationRequest => {
  const both = model.period === 'AMBOS'

  const productClasses = products
    .flatMap(p =>
      p.classes
        ?.split(/,/)
        .map(c => c.trim().toUpperCase())
        .filter(Boolean) || []
    )
    .filter(Boolean)

  const filteredActives = model.actives.filter(active =>
    productClasses.includes(active.name.toUpperCase().trim())
  )

  const subclass = model.subclass?.length ? model.subclass.map(sub => sub.id) : []

  return {
    patients,
    consultation: {
      id_Body: 2,
      id_TypeUse: 1,
      consultName: consultName || '',
      daytime: model.period === 'DIURNO' || both,
      nighttime: model.period === 'NOTURNO' || both,
      pregnant: model.pregnant || false,
      vegan: model.vegan || false,
      organic: model.organic || false,
      vehicles_id: model.vehicles?.id,
      observation: observation,
      active: filteredActives.map(act => act.id),
      subclass: subclass,
      product: products
        .filter(prod => prod.idProduct !== undefined || prod.id !== undefined)
        .map(prod => ({
          id: (prod.idProduct ?? prod.id) as number,
          percentage: Number(prod.percentage) || 0,
        })),
      observations: observations,
    }
  }
}

// endregion

export const getPdf = (model: QueryModel, userId: number, observation: string, observations: string[], products: ProductModel[], consultName?: string) => {
  const query = queryToPdf(model, userId, observation, observations, products, consultName)
  return http.post(`/v2/pdf/body`, query)
}

export const getHtml = (model: QueryModel, userId: number, observation: string, observations: string[], products: ProductModel[], consultName?: string) => {
  const query = queryToPdf(model, userId, observation, observations, products, consultName)
  return http.post(`/v2/pdf/body/html`, query)
}

export const queryToPdf = (model: QueryModel, userId: number, observation: string, observations: string[], products: ProductModel[], consultName?: string) => {
  const both = model.period === 'AMBOS'

  return {
    daytime: model.period === 'DIURNO' || both,
    nighttime: model.period === 'NOTURNO' || both,
    pregnant: model.pregnant || false,
    vegan: model.vegan || false,
    organic: model.organic || false,
    user: userId,
    patient: model.patientId === 0 ? undefined : model.patientId,
    products: products?.map((prod) => {
      return {
          name: prod.productName,
          percentage: prod.percentage || 0,
          showConcentration: prod.showConcentration,
      } as Product
    }),
    vehicleName: model.vehicles?.name,
    observation: observation,
    observations: observations,
    consultName: consultName,
  } as PDFData
}
